import { useMemo } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCube, faUser } from "@fortawesome/pro-regular-svg-icons";

import {
  FieldDefinition,
  FieldsOption,
} from "src/base-components/FilterPopover/types";
import { PillVariants } from "src/base-components/Pill";
import { getSchemaTypeIcon } from "src/base-components/SchemaTypeSelector";
import { SELECT_DIVIDER } from "src/base-components/Select";
import { useQueueColumnSchemas } from "src/cases/queues/hooks/useQueueColumnSchemas";
import {
  buildStatusPairOptions,
  STATUS_FIELD_KEY,
} from "src/cases/queues/statusFilterTransform";
import {
  CaseQueueColumnKey,
  CaseQueueColumnSchema,
  CaseQueueColumnSchemas,
} from "src/cases/queues/types";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { EVENT_FALLBACK_ICON, getEventIcon } from "src/eventsCatalogue/utils";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { TYPE_ICONS } from "src/utils/constants";

const BUILTIN_FILTER_FIELDS: FieldDefinition[] = [
  {
    key: "assignee_id",
    value: "Assignee",
    type: "assignee",
    icon: faUser,
  },
  {
    key: "created_at",
    value: "Created At",
    type: "datetime",
    icon: TYPE_ICONS.datetime,
  },
  {
    key: "updated_at",
    value: "Updated At",
    type: "datetime",
    icon: TYPE_ICONS.datetime,
  },
];

const mapColumnSchemaToFieldDefinition = (
  _key: CaseQueueColumnKey,
  schema: CaseQueueColumnSchema,
  icon: IconProp,
): FieldDefinition => {
  const meta =
    schema.type === "entity_ref"
      ? { entityType: schema.entity_type }
      : schema.type === "event_ref"
        ? { eventType: schema.event_type }
        : undefined;

  const base: FieldDefinition = {
    key: `properties.${schema.__key}+${schema.type}`,
    value: schema.display_name,
    type: schema.type,
    icon,
    meta,
  };

  if (schema.type === "enum" || schema.type === "tags") {
    return {
      ...base,
      options:
        base.key === STATUS_FIELD_KEY
          ? buildStatusPairOptions(schema.options)
          : schema.options.map((option) => ({
              key: option.value,
              value: option.displayName || option.value,
              pillVariant: option.color as PillVariants | undefined,
            })),
    };
  }

  return base;
};

const mapColumnSchemasToFieldDefinitions = (
  schemas: CaseQueueColumnSchemas,
  resolveIcon: (schema: CaseQueueColumnSchema) => IconProp,
): FieldDefinition[] => {
  return (
    Object.entries(schemas) as [CaseQueueColumnKey, CaseQueueColumnSchema][]
  )
    .filter(
      (entry): entry is [CaseQueueColumnKey, CaseQueueColumnSchema] =>
        entry[1] !== undefined,
    )
    .map(([key, schema]) =>
      mapColumnSchemaToFieldDefinition(key, schema, resolveIcon(schema)),
    );
};

type UseFilterFieldsResult = {
  fields: FieldsOption[];
  isLoading: boolean;
};

export const useFilterFields = (
  queueId: string | undefined,
): UseFilterFieldsResult => {
  const { columnSchemas, isLoading: isLoadingSchemas } =
    useQueueColumnSchemas(queueId);

  const featureGates = useWorkspaceFeatureGates();
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
    options: { enabled: featureGates.entitiesEnabled },
  });
  const { data: eventSchemas } = useEventSchemas();

  const fields = useMemo(() => {
    const resolveIcon = (columnSchema: CaseQueueColumnSchema): IconProp => {
      if (columnSchema.type === "entity_ref") {
        const entitySchema = findSchema(
          columnSchema.entity_type,
          entitySchemas,
        );
        return entitySchema ? getSchemaIcon(entitySchema) : faCube;
      }

      if (columnSchema.type === "event_ref") {
        const eventSchema = eventSchemas?.find(
          (e) => e.event_type === columnSchema.event_type,
        );
        return eventSchema ? getEventIcon(eventSchema) : EVENT_FALLBACK_ICON;
      }

      return getSchemaTypeIcon(columnSchema.type);
    };

    const templateFields = mapColumnSchemasToFieldDefinitions(
      columnSchemas,
      resolveIcon,
    );

    return [...BUILTIN_FILTER_FIELDS, SELECT_DIVIDER, ...templateFields];
  }, [columnSchemas, entitySchemas, eventSchemas]);

  return { fields, isLoading: isLoadingSchemas };
};
